import { Shell, type NavItem } from "../lib/Shell.js";
import { useAuth } from "../lib/auth.js";
import { LicenseBanner, LicenseLapsed, useLicenseStatus } from "../lib/license.js";
import { useJobsEnabled } from "../lib/jobs.js";

/** Invoices are owner-only (RLS), so staff don't get the link. */
const ITEMS: Array<NavItem & { ownerOnly?: boolean; jobsOnly?: boolean }> = [
  { to: "/app", label: "Dashboard", icon: "dashboard" },
  { to: "/app/bookings", label: "Bookings", icon: "bookings" },
  { to: "/app/new-booking", label: "New booking", icon: "newBooking" },
  { to: "/app/calls", label: "Calls", icon: "calls" },
  { to: "/app/callbacks", label: "Callbacks", icon: "callbacks" },
  { to: "/app/jobs", label: "Jobs", icon: "jobs", jobsOnly: true },
  { to: "/app/customers", label: "Customers", icon: "customers" },
  { to: "/app/services", label: "Services & staff", icon: "services", separated: true },
  { to: "/app/rules", label: "Receptionist rules", icon: "rules", ownerOnly: true },
  { to: "/app/invoices", label: "Invoices", icon: "invoices", ownerOnly: true },
  { to: "/app/settings", label: "Business settings", icon: "settings", ownerOnly: true },
  { to: "/app/security", label: "Security", icon: "security", separated: true },
];

/** A staff login (linked to one staff member) sees only that person's bookings and hours. */
const STAFF_ITEMS: NavItem[] = [
  { to: "/app/bookings", label: "My bookings", icon: "bookings" },
  { to: "/app/new-booking", label: "New booking", icon: "newBooking" },
  { to: "/app/my-hours", label: "My hours", icon: "services" },
  { to: "/app/security", label: "Security", icon: "security", separated: true },
];

const ROLE_NAME = { owner: "Owner", manager: "Manager", staff: "Staff", viewer: "Viewer" } as const;

export function AppShell() {
  const { activeBusinessId, memberships, isPlatformAdmin } = useAuth();
  const { status, loaded } = useLicenseStatus(activeBusinessId);
  const membership = memberships.find((m) => m.business_id === activeBusinessId);
  const isStaffLogin = membership?.role === "staff" && !isPlatformAdmin;
  const jobsEnabled = useJobsEnabled(activeBusinessId);
  const items = isStaffLogin ? STAFF_ITEMS : ITEMS.filter((i) => (!i.ownerOnly || membership?.role === "owner") && (!i.jobsOnly || jobsEnabled === true));

  if (activeBusinessId && !loaded) return <div className="rc-loading" style={{ padding: 40 }}>Loading...</div>;
  // A lapsed licence replaces the whole portal for the client. Platform admins still get in, so support can look around.
  if (status?.state === "lapsed" && !isPlatformAdmin) return <LicenseLapsed status={status} businessName={membership?.business_name ?? null} />;

  // A read-only (free online booking) login is pointed at the developer to upgrade; the address is the support email set in Admin > Developer info.
  const upgradeNote =
    membership?.role === "viewer" && !isPlatformAdmin ? (
      <>
        Contact{" "}
        {status?.supportEmail ? <a href={`mailto:${status.supportEmail}?subject=Upgrade%20to%20phone%20booking`}>{status.supportEmail}</a> : "your developer"} to upgrade to the full
        version with phone booking automation.
      </>
    ) : null;
  const roleName = membership ? ROLE_NAME[membership.role] : isPlatformAdmin ? "Platform admin" : "Receptionist";
  return <Shell title={membership?.business_name ?? (activeBusinessId ? "Business" : "Receptionist")} subtitle={roleName} items={items} note={upgradeNote} banner={<LicenseBanner status={status} />} />;
}
